/**
 * Metrics § Overview (§8.4, redesign § Overview).
 *
 * Six stat islands double as the chart's series toggles: each shows a total plus the
 * per-counted-post avg/median sub-line, and clicking it toggles its series (active = solid series
 * color). Each island carries its own corner (i) with that stat's server tooltip plus the avg and
 * median formulas, all rendered verbatim (§12.5). The time-series chart moved here from Posting
 * schedule — its buckets still come from the `postingSchedule` DTO section (visual placement only).
 *
 * Group views additionally show the per-member breakdown as its own table island below (§8.8);
 * the pooled/per-author asymmetry is stated by the server tooltips, never implied. That table is
 * interactive — sortable, and a row click filters the whole view by that member — so its state is
 * owned by `MetricsMode` (which survives the refetch this section does not) and passed down here.
 */

import { useState, type CSSProperties, type ReactNode } from 'react';
import type {
  AggregateMetricKey,
  OverviewData,
  PostingScheduleData,
  Section,
} from '@lw/shared';
import { Chart, Modal, TooltipPopover, cx, type ChartSeries } from '../../../components';
import { formatInteger } from '../../../lib/format';
import { PerMemberTable, type PerMemberSort, type PerMemberSortKey } from './PerMemberTable';

type OverviewSeriesKey = 'posts' | AggregateMetricKey;

type OverviewSeriesDef = {
  key: OverviewSeriesKey;
  label: string;
  /** Resolved series token (index.css) — the stat tint and the legend swatch. */
  color: string;
  /** Overrides `color` for the plotted mark only, where the stat colour is too heavy. */
  chartColor?: string;
  type: 'bar' | 'line';
};

/** Posts render as bars, every other metric as a line (redesign [D]). */
const OVERVIEW_SERIES: readonly OverviewSeriesDef[] = [
  {
    key: 'posts',
    label: 'Counted posts',
    color: 'var(--series-posts)',
    // Bars are the backdrop the lines are read against — pale, so they never compete.
    chartColor: 'var(--series-posts-bar)',
    type: 'bar',
  },
  { key: 'reactions', label: 'Reactions', color: 'var(--series-reactions)', type: 'line' },
  { key: 'comments', label: 'Comments', color: 'var(--series-comments)', type: 'line' },
  { key: 'shares', label: 'Shares', color: 'var(--series-shares)', type: 'line' },
  { key: 'saves', label: 'Saves', color: 'var(--series-saves)', type: 'line' },
  { key: 'totalEngagement', label: 'Total eng.', color: 'var(--series-total)', type: 'line' },
];

/**
 * The per-stat corner (i): the server's total tooltip, with the avg and median formulas appended
 * as an extra section. Every string comes from the DTO — nothing is composed here (§12.5).
 */
function StatInfo({ data, series }: { data: OverviewData; series: OverviewSeriesDef }) {
  const { key, label } = series;
  const tooltip = data.totals[key].tooltip;
  if (tooltip === undefined) return null;

  const perPost = key === 'posts' ? null : data.perPost[key];
  const avg = perPost?.avg.tooltip?.formula;
  const median = perPost?.median.tooltip?.formula;

  return (
    <TooltipPopover
      tooltip={tooltip}
      align="left"
      label={`How ${label} is calculated`}
      extra={
        avg !== undefined || median !== undefined ? (
          <span className="tip__section">
            <span className="tip__heading">Per counted post</span>
            {avg !== undefined ? <span className="ov-stat__formula">{avg}</span> : null}
            {median !== undefined ? <span className="ov-stat__formula">{median}</span> : null}
          </span>
        ) : undefined
      }
    />
  );
}

export type OverviewSectionProps = {
  data: OverviewData;
  /** Chart buckets come from the posting-schedule DTO section (redesign: placement only). */
  schedule: Section<PostingScheduleData> | undefined;
  /** Opens the popup listing that bucket's posts; buckets with no posts stay inert. */
  onBucketClick?: (index: number) => void;
  mediaEnabled: boolean;
  /** The view's `authorIds` filter — the highlighted per-member rows (group views only). */
  activeAuthorIds: number[];
  onToggleMember: (memberId: number) => void;
  perMemberSort: PerMemberSort;
  onPerMemberSort: (key: PerMemberSortKey) => void;
  perMemberFullscreen: boolean;
  onPerMemberFullscreenChange: (open: boolean) => void;
};

export function OverviewSection({
  data,
  schedule,
  onBucketClick,
  mediaEnabled,
  activeAuthorIds,
  onToggleMember,
  perMemberSort,
  onPerMemberSort,
  perMemberFullscreen,
  onPerMemberFullscreenChange,
}: OverviewSectionProps) {
  const [active, setActive] = useState<OverviewSeriesKey[]>(['posts', 'reactions']);

  const toggle = (key: OverviewSeriesKey) =>
    setActive((current) =>
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key],
    );

  const buckets = schedule !== undefined && schedule.state === 'ok' ? schedule.data.series : [];
  const activeSeries = OVERVIEW_SERIES.filter((series) => active.includes(series.key));
  const chartSeries: ChartSeries[] = activeSeries.map((series) => ({
    key: series.key,
    type: series.type,
    color: series.chartColor ?? series.color,
    values: buckets.map((bucket) => bucket[series.key]),
  }));

  const bucketClickable = (index: number): boolean => (buckets[index]?.posts ?? 0) > 0;

  const renderTooltip = (index: number): ReactNode => {
    const bucket = buckets[index];
    if (bucket === undefined) return null;
    return (
      <div>
        {/* The heading names the bucket in full ("6 – 12 Aug 2026"); the axis keeps `label`. */}
        <div className="t-label">{bucket.fullLabel}</div>
        {activeSeries.map((series) => {
          const value = bucket[series.key];
          if (value === null) return null;
          return (
            <div className="t-row" key={series.key}>
              <span className="t-label">
                {/* Same expression the mark uses, so the posts dot matches the pale bar. */}
                <span
                  className="t-dot"
                  style={{ background: series.chartColor ?? series.color }}
                  aria-hidden
                />
                {series.label}
              </span>
              {/* Separators only: presenting a raw chart value is rendering, not client math (§12.5) —
                  the same latitude `formatTick` takes on the axis. */}
              <span className="t-value">{formatInteger(value)}</span>
            </div>
          );
        })}
        {onBucketClick !== undefined && bucketClickable(index) ? (
          <div className="t-hint">Click to show posts</div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="card">
        <div className="card__body">
          {/* Islands are divs, not buttons: the corner (i) is itself a button and must not nest. */}
          <div className="ov-stats">
            {OVERVIEW_SERIES.map((series) => {
              const { key } = series;
              const isActive = active.includes(key);
              return (
                <div
                  key={key}
                  className={cx('ov-stat', isActive && 'is-active')}
                  style={{ '--stat-color': series.color } as CSSProperties}
                >
                  <button
                    type="button"
                    className="ov-stat__btn"
                    aria-pressed={isActive}
                    onClick={() => toggle(key)}
                  >
                    <span className="ov-stat__label">{series.label}</span>
                    <span className="ov-stat__value">{data.totals[key].display}</span>
                    {key !== 'posts' ? (
                      <span className="ov-stat__sub">
                        {`${data.perPost[key].avg.display} avg / ${data.perPost[key].median.display} med`}
                      </span>
                    ) : null}
                  </button>
                  <StatInfo data={data} series={series} />
                </div>
              );
            })}
          </div>

          {buckets.length > 0 ? (
            <div className="ov-chart">
              <Chart
                labels={buckets.map((bucket) => bucket.label)}
                series={chartSeries}
                height={300}
                renderTooltip={renderTooltip}
                onBucketClick={onBucketClick}
                isBucketClickable={bucketClickable}
                ariaLabel="Posts and engagement per bucket"
              />
            </div>
          ) : schedule !== undefined && schedule.state === 'empty' ? (
            <p className="ov-chart muted">{schedule.reason}</p>
          ) : null}
        </div>
      </div>

      {data.perMember !== undefined && data.perMember.length > 0 ? (
        <>
          <div className="metrics-subhead">
            <h3 className="metrics-subtitle">Per member</h3>
            <button
              type="button"
              className="table-action"
              onClick={() => onPerMemberFullscreenChange(true)}
            >
              Full screen
            </button>
          </div>
          <div className="card card--table">
            <PerMemberTable
              rows={data.perMember}
              sort={perMemberSort}
              onSort={onPerMemberSort}
              activeAuthorIds={activeAuthorIds}
              onToggleMember={onToggleMember}
              mediaEnabled={mediaEnabled}
            />
          </div>

          {/* The same table, same lifted state: the popup is room to read it, not a second
              surface. The one difference is the width it buys — headers spelled out. */}
          <Modal
            open={perMemberFullscreen}
            title="Per member"
            size="full"
            flushBody
            className="pm-modal"
            onClose={() => onPerMemberFullscreenChange(false)}
          >
            <PerMemberTable
              rows={data.perMember}
              sort={perMemberSort}
              onSort={onPerMemberSort}
              activeAuthorIds={activeAuthorIds}
              onToggleMember={onToggleMember}
              mediaEnabled={mediaEnabled}
              fullLabels
            />
          </Modal>
        </>
      ) : null}
    </>
  );
}

export default OverviewSection;
